import { useContext, useEffect, useState, useCallback } from "react";
import { useStats } from "react-instantsearch";
import {
  Box,
  Button,
  HStack,
  Icon,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { ContactImportsTable } from "./ContactImportsTable";
import { KYDContactImportJob } from "@common/interfaces/KYDCampaign";
import { KYDScope } from "@common/interfaces/ICognito";
import actions from "src/actions";
import { useDialog } from "src/Common/Dialog";
import ImportContactsDialog from "../Contacts/ImportFansDialog";
import { useSearchParams } from "react-router-dom";
import { usePolling } from "src/Manager/EventDetail/Nav/Marketing/usePolling";
import { useMultiPolling } from "src/Manager/EventDetail/Nav/Marketing/useMultiPolling";

const ContactsImport = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const dialog = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contactImports, setContactImports] = useState<KYDContactImportJob[]>(
    []
  );
  const importContacts = searchParams.get("import_contacts") === "true";

  const { start: pollForImportStatus } = useMultiPolling({
    maxRuns: 5,
    waitFor: 10000,
    poll: useCallback(
      (id: string) => actions.ADMIN.fetchContactImportJob(id),
      []
    ),
    checkResult: (result, id: string) => {
      if (!result) {
        console.log("No result received for id:", id);
        return false;
      }

      const importJob = contactImports.find((importJob) => importJob.id === id);
      const isComplete = result.status === "COMPLETED";

      console.log("Polling check:", {
        id,
        currentStatus: result.status,
        previousStatus: importJob?.status,
        isComplete,
      });

      const shouldUpdate = result.status !== importJob?.status;
      if (shouldUpdate) {
        setContactImports((prev) =>
          prev.map((importJob) => (importJob.id === id ? result : importJob))
        );
      }

      return isComplete;
    },
  });

  const fetchContactImports = useCallback(async () => {
    try {
      const response = await actions.ADMIN.fetchContactImportJobs();

      if (response) {
        setContactImports(response);
        const hasPendingImports = response?.filter((importJob) =>
          ["PROCESSING", "SCHEDULED"].includes(importJob.status)
        );

        if (hasPendingImports.length > 0) {
          hasPendingImports.forEach((importJob) => {
            pollForImportStatus(importJob.id);
          });
        }
      }
    } catch (e) {
      dialog(e.message);
    }
  }, [dialog, pollForImportStatus]);

  useEffect(() => {
    fetchContactImports();
  }, []);

  useEffect(() => {
    if (importContacts) {
      setIsImportDialogOpen(true);
      setSearchParams({});
    }
  }, [importContacts, searchParams, setSearchParams]);

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  const onImportContactsStarted = useCallback(
    (data: {
      data: any;
      response: {
        id?: string;
        message: string;
      };
    }) => {
      if (data.response.id) {
        fetchContactImports();
      }
    },
    [fetchContactImports]
  );

  const handleOpenImportDialog = useCallback(() => {
    setIsImportDialogOpen(true);
  }, []);

  const handleCloseImportDialog = useCallback(() => {
    setIsImportDialogOpen(false);
  }, []);

  return (
    <VStack w="100%" alignItems="flex-start">
      {isImportDialogOpen && (
        <ImportContactsDialog
          isOpen={isImportDialogOpen}
          onSubmit={onImportContactsStarted}
          close={handleCloseImportDialog}
        />
      )}
      <HStack
        pb={6}
        alignItems="center"
        justifyContent="space-between"
        w="100%"
      >
        <HStack alignItems="center" gap={2}>
          <Text
            color="#1A202C"
            fontFamily="Work Sans"
            fontSize="32px"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="30px"
            letterSpacing="-0.96px"
          >
            Imports
          </Text>
          <Text
            color="rgba(26, 32, 44, 0.50)"
            fontFamily="Work Sans"
            fontSize="32px"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="30px"
            letterSpacing="-0.96px"
          >
            {contactImports.length}
          </Text>
        </HStack>
        <Button
          rightIcon={<Icon as={AddIcon} />}
          borderRadius="full"
          onClick={handleOpenImportDialog}
          bg="black"
          gap={1}
          textColor="white"
          _hover={{ bg: "gray.800" }}
          fontSize="16px"
        >
          <Text pl={2}>New Import</Text>
        </Button>
      </HStack>

      <Box w="100%" overflowX="auto" mt={6}>
        <ContactImportsTable permissions={permissions} rows={contactImports} />
      </Box>
    </VStack>
  );
};

const ContactsStatsWithLoading = () => {
  const stats = useStats();

  if (!stats.processingTimeMS) {
    return <Spinner size="sm" />;
  }
  return (
    <Text
      textAlign="left"
      color="#1A202C"
      fontSize="24px"
      fontWeight={600}
      lineHeight="120%"
      letterSpacing="-0.72px"
    >
      {stats.nbHits} Contacts
    </Text>
  );
};

export default ContactsImport;
